import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { capacidades } from "@/lib/capacidades";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReadinessPanel } from "@/components/admin/readiness";
import { fmtFecha, fmtTimestamp, calcEdad } from "@/lib/format";
import { filasInstrucciones } from "@/lib/instruccionesResumen";
import { etiquetaTipoDispositivo } from "@/lib/dispositivoLabel";
import PersonForm, { etiquetaCama } from "@/components/forms/PersonForm";
import PlanRespuestaDialog from "@/components/persona/PlanRespuestaDialog";
import ProgramadosSection from "@/components/persona/ProgramadosSection";
import PersonaAccesosApp from "@/components/admin/PersonaAccesosApp";
import { Dot } from "@/components/StatusBits";
import {
  ArrowLeft, Pencil, User2, Phone, ClipboardList, ShieldAlert, Cpu, CheckCircle2, AlertTriangle,
} from "lucide-react";

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{value || "—"}</p>
    </div>
  );
}

// Object page de PERSONA (v2.9.8). Readiness (3 dimensiones) + edición POR SECCIÓN (no abre el
// wizard completo). Guardar/Cancelar quedan siempre accesibles (footer sticky en PersonForm).
export default function PersonaObjectPage({ persona, sitio, dispositivos, contactos, clientes, onBack, onSaved, refetchContactos, initialTab, camas = [], zonas = [] }) {
  const { user } = useAuth();
  // C-1/C-3: la edición se habilita POR SECCIÓN con la misma autorización que exige el endpoint
  // que esa sección escribe (ver capacidades.js). El monitorista interno conserva la edición
  // operativa que el backend le permite y no se le ofrece lo que devolvería 403.
  const caps = capacidades(user);
  const puedeSeccion = {
    datos: caps.editarDatosPersona,
    contactos: caps.gestionarContactos,
    instrucciones: caps.editarInstruccionesPersona,
    dispositivos: caps.asociarDispositivosPersona,
  };
  const [editSection, setEditSection] = useState(null); // "datos"|"contactos"|"instrucciones"|"dispositivos"
  // UI-014/UI-015: la ubicación interna (habitación/cama) pertenece al sitio institucional.
  const esInstitucional = (sitio?.modalidad || "residencial") === "institucional";
  const camaVinculada = (camas || []).find((c) => c.id === persona.cama_id);
  const [planOpen, setPlanOpen] = useState(false);
  const p = persona;
  const ins = p.instrucciones || {};
  const cmap = {};
  (contactos || []).forEach((c) => (cmap[c.id] = c));
  const resolveHybrid = (h) => {
    if (!h) return "Sin definir";
    if (h.contacto_id) return cmap[h.contacto_id]?.nombre_completo || "(contacto)";
    if (h.nombre || h.telefono) return `${h.nombre || ""}${h.telefono ? " · " + h.telefono : ""}`.trim();
    return "Sin definir";
  };

  const togglePersona = async () => {
    try {
      if (p.estado === "Inactiva") await api.post(`/personas/${p.id}/activar`);
      else await api.post(`/personas/${p.id}/desactivar`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const SectionCard = ({ title, section, children, icon: Icon }) => (
    <section className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-medium text-[#012472] flex items-center gap-2">{Icon && <Icon strokeWidth={1.6} className="h-4 w-4" />} {title}</h3>
        {section && puedeSeccion[section] && (
          <Button variant="outline" size="sm" className="rounded-lg" data-testid={`edit-section-${section}`} onClick={() => setEditSection(section)}>
            <Pencil strokeWidth={1.5} className="h-4 w-4 mr-1" /> Editar
          </Button>
        )}
      </div>
      {children}
    </section>
  );

  const planDim = p.readiness?.plan_respuesta;
  const nombrePersona = (p.nombre_completo || p.nombre || "").trim();
  const subtitulo = [
    p.fecha_nacimiento ? `${fmtFecha(p.fecha_nacimiento)} · ${calcEdad(p.fecha_nacimiento)} años` : "",
    p.idioma_preferido ? `Idioma: ${p.idioma_preferido}` : "",
    esInstitucional && (p.habitacion || "") ? p.habitacion : "",
  ].filter(Boolean).join(" · ");

  return (
    <div className="tema-noche-auto space-y-5" data-testid={`persona-object-page-${p.id}`}>
      <button onClick={onBack} data-testid="persona-back" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#012472] transition-colors">
        <ArrowLeft strokeWidth={1.5} className="h-4 w-4" /> Volver al sitio
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          {/* GC94: el read-model INTERNO publica `nombre_completo`; el DTO externo autorizado
              publica `nombre`. El expediente presenta el nombre real con cualquiera de los dos y
              el subtítulo sólo enuncia los campos que el read-model entrega (sin «Idioma:» vacío). */}
          <h1 data-testid="persona-object-nombre" className="text-2xl font-semibold tracking-tight text-[#012472]">{nombrePersona || "(sin nombre)"}</h1>
          {subtitulo && <p className="text-sm text-slate-500 mt-1">{subtitulo}</p>}
        </div>
        {caps.cambiarEstadoPersona && (
          <Button variant="ghost" size="sm" className="rounded-lg text-slate-500" data-testid="toggle-person-btn" onClick={togglePersona}>
            {p.estado === "Inactiva" ? "Activar" : "Desactivar"}
          </Button>
        )}
      </div>

      <Tabs defaultValue={initialTab || "resumen"} className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="resumen" data-testid="persona-tab-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="contactos" data-testid="persona-tab-contactos">Contactos</TabsTrigger>
          <TabsTrigger value="instrucciones" data-testid="persona-tab-instrucciones">Instrucciones</TabsTrigger>
          <TabsTrigger value="dispositivos" data-testid="persona-tab-dispositivos">Dispositivos</TabsTrigger>
          <TabsTrigger value="plan" data-testid="persona-tab-plan">Plan de respuesta</TabsTrigger>
          <TabsTrigger value="programados" data-testid="persona-tab-programados">Programados</TabsTrigger>
          {/* PROV-01 · entrada contextual: sólo supervisor interno, igual que la superficie
              /api/app-admin. No modifica el modelo ni los endpoints del expediente. */}
          {caps.supervisor && (
            <TabsTrigger value="accesos_app" data-testid="persona-tab-accesos-app">Accesos App</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="resumen" className="mt-5 space-y-5">
          <SectionCard title="Readiness">
            <ReadinessPanel readiness={p.readiness} testId={`persona-readiness-${p.id}`} />
          </SectionCard>
          <SectionCard title="Datos de la persona" section="datos" icon={User2}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Info label="Nombre completo" value={nombrePersona} />
              <Info label="Fecha de nacimiento" value={p.fecha_nacimiento ? fmtFecha(p.fecha_nacimiento) : ""} />
              <Info label="Teléfono" value={p.telefono} />
              <Info label="Idioma" value={p.idioma_preferido} />
              <Info label="Paquete (comercial)" value={p.paquete} />
              <Info label="Servicios contratados" value={(p.servicios_contratados || []).map((s) => ({ recordatorios: "Recordatorios", monitoreo_sensores: "Monitoreo con sensores" }[s] || s)).join(" · ")} />
              {esInstitucional && <Info label="Habitación / cama (referencia)" value={p.habitacion} />}
              {esInstitucional && <Info label="Cama registrada" value={camaVinculada ? etiquetaCama(camaVinculada, zonas) : ""} />}
            </div>
            {p.condiciones && <div className="mt-3"><Info label="Condiciones que afectan la verificación" value={p.condiciones} /></div>}
          </SectionCard>
        </TabsContent>

        <TabsContent value="contactos" className="mt-5">
          <SectionCard title={`Contactos de escalamiento (${(p.contactos || []).length})`} section="contactos" icon={User2}>
            {(p.contactos || []).length === 0 ? (
              <p className="text-sm text-amber-600">Sin contactos capturados.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(p.contactos || []).map((rel, i) => (
                  <div key={i} className="rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center gap-2">
                      <span className="h-6 w-6 rounded-full bg-[#01CBE3]/10 text-[#012472] text-xs flex items-center justify-center font-medium">{rel.orden || i + 1}</span>
                      <p className="font-medium text-slate-800 text-sm truncate">{rel.contacto?.nombre_completo || "(sin nombre)"}</p>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{rel.relacion_familiar}</p>
                    {rel.contacto?.telefono && <p className="text-xs text-slate-600 flex items-center gap-1 mt-1"><Phone strokeWidth={1.5} className="h-3 w-3" /> {rel.contacto.telefono}</p>}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="instrucciones" className="mt-5">
          <SectionCard title="Instrucciones operativas" section="instrucciones" icon={ClipboardList}>
            {/* UI-017: se leen los ONCE apartados que el editor escribe (antes faltaban 4, 6, 8 y
                10). «No» se muestra como «No», y lo no contestado como «Sin contestar». */}
            <div className="grid gap-4 sm:grid-cols-2" data-testid="persona-instrucciones-lectura">
              {filasInstrucciones(ins, {
                audioNoAplica: p.readiness?.sitio_tiene_audio === false,
                resolverHibrido: resolveHybrid,
              }).map((fila) => (
                fila.destacado ? (
                  <div key={fila.n} data-testid={`persona-instr-${fila.n}`}
                    className="sm:col-span-2 rounded-xl border-2 border-[#01CBE3]/40 bg-[#01CBE3]/5 p-4">
                    <p className="text-sm font-medium text-[#012472] flex items-center gap-2">
                      <ShieldAlert strokeWidth={1.75} className="h-4 w-4 text-[#01CBE3]" /> {fila.label}
                    </p>
                    <p className="text-base text-slate-800 mt-1 font-medium">{fila.valor}</p>
                  </div>
                ) : (
                  <div key={fila.n} data-testid={`persona-instr-${fila.n}`} className={fila.ancho ? "sm:col-span-2" : ""}>
                    <Info label={fila.label} value={fila.valor} />
                  </div>
                )
              ))}
            </div>
            {p.instrucciones_actualizado_por && (
              <p className="text-xs text-slate-400 border-t border-gray-100 pt-3 mt-3">
                Última modificación: {p.instrucciones_actualizado_por} · {fmtTimestamp(p.instrucciones_actualizado_en)}
              </p>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="dispositivos" className="mt-5">
          <SectionCard title="Dispositivos que la monitorean" section="dispositivos" icon={Cpu}>
            {(p.dispositivos_asociados || []).length === 0 ? (
              <p className="text-sm text-amber-600">Ningún dispositivo asociado a esta persona.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {p.dispositivos_asociados.map((d) => (
                  <span key={d.id} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-gray-100 bg-slate-50 px-3 py-1.5 text-slate-700">
                    <Dot color={d.conectividad === "Fuera de línea" ? "gray" : "green"} />
                    {etiquetaTipoDispositivo(d.tipo)} — {d.ubicacion || "sin ubicación"}
                  </span>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="plan" className="mt-5">
          <SectionCard title="Plan de respuesta" icon={ClipboardList}>
            <div className={`flex items-center gap-2 text-sm font-medium ${planDim?.completo ? "text-emerald-600" : "text-amber-700"}`}>
              {planDim?.completo ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {planDim?.completo ? "Plan de respuesta OK · commissioning LISTO" : "Plan de respuesta incompleto"}
            </div>
            {planDim && !planDim.completo && (planDim.faltantes || []).length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-amber-700 list-disc pl-5" data-testid="plan-faltantes">
                {planDim.faltantes.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            )}
            <div className="pt-2">
              <Button data-testid="edit-plan-btn" size="sm" className="rounded-lg bg-[#012472] hover:bg-[#012472]/90 text-white"
                onClick={() => setPlanOpen(true)} disabled={!caps.editarPlanRespuesta}>
                {caps.editarPlanRespuesta ? "Editar plan de respuesta" : "No tienes permiso para editar el plan"}
              </Button>
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="programados" className="mt-5">
          <ProgramadosSection personaId={p.id} puedeGestionar={caps.gestionarProgramados} />
        </TabsContent>

        {caps.supervisor && (
          <TabsContent value="accesos_app" className="tema-noche-auto mt-5">
            <SectionCard title="Accesos App de esta persona" icon={User2}>
              <PersonaAccesosApp personaId={p.id} />
            </SectionCard>
          </TabsContent>
        )}
      </Tabs>

      {editSection && (
        <PersonForm
          open={!!editSection}
          onOpenChange={(v) => { if (!v) setEditSection(null); }}
          persona={p}
          sitioId={sitio.id}
          dispositivos={dispositivos}
          clientes={clientes}
          contactos={contactos}
          refetchContactos={refetchContactos}
          sections={[editSection]}
          modalidad={sitio?.modalidad || "residencial"}
          camas={camas}
          zonas={zonas}
          puedeAsociarDispositivos={caps.asociarDispositivosPersona}
          puedeCrearContacto={caps.crearContacto}
          title={`Editar · ${{ datos: "Datos", contactos: "Contactos", instrucciones: "Instrucciones", dispositivos: "Dispositivos" }[editSection]}`}
          onSaved={async () => { await onSaved(); setEditSection(null); }}
        />
      )}
      <PlanRespuestaDialog open={planOpen} onOpenChange={(v) => { setPlanOpen(v); if (!v) onSaved(); }} personaId={p.id} />
    </div>
  );
}
